import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, message, Select, Space, Typography, Upload } from "antd";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permissions";
import { storageApi } from "../../../services/storageApi";
import { appInfoService } from "../../../services/AppInfoService";
import { RootState } from "../../../store";
import { AppInfo } from "../../../types/app-info.types";

const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;

interface UploadFormData {
  fileType: string;
  fileName?: string;
  version: string;
  versionCode: number;
}

const UploadApkPage: React.FC = () => {
  const [form] = Form.useForm<UploadFormData>();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [appInfos, setAppInfos] = useState<AppInfo[]>([]);
  const [appInfoLoading, setAppInfoLoading] = useState(false);
  const navigate = useNavigate();
  const { permissions } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    fetchAppInfos();
  }, []);

  const fetchAppInfos = async () => {
    try {
      setAppInfoLoading(true);
      const response = await appInfoService.getAll();
      if (response.status === 'SUCCESS' && response.data) {
        setAppInfos(response.data);
      }
    } catch (error) {
      message.error('Failed to load app information');
    } finally {
      setAppInfoLoading(false);
    }
  };

  const hasPermission = (permissionKey: string) => {
    return permissions.some((p) => p.name === permissionKey);
  };

  const canUploadApps = hasPermission('UPLOAD_APPS');

  if (!canUploadApps) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="You don't have permission to upload APK files."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const beforeUpload = (file: File) => {
    const isApk = file.type === 'application/vnd.android.package-archive' || file.name.endsWith('.apk');
    if (!isApk) {
      message.error('You can only upload APK files!');
      return false;
    }
    
    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      message.error('APK file must be smaller than 100MB!');
      return false;
    }

    setSelectedFile(file);
    return false; // Prevent auto upload
  };

  const onFinish = async (values: UploadFormData) => {
    if (!selectedFile) {
      message.error('Please select an APK file to upload');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      const downloadUrl = await storageApi.uploadApk(
        selectedFile,
        values.fileType,
        values.version || '',
        values.versionCode || 0,
        values.fileName,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      message.success('APK file uploaded successfully!');
      
      // Reset form
      form.resetFields();
      setSelectedFile(null);
      setUploadProgress(0);

      // Show success message with download link
      message.success(
        <div>
          <div>APK uploaded successfully!</div>
          <div>
            <Text type="secondary">Download URL: </Text>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
              {downloadUrl}
            </a>
          </div>
        </div>,
        10
      );

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload APK file';
      message.error(errorMessage);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const fileTypeOptions = appInfos.map(appInfo => ({
    value: appInfo.appName,
    label: appInfo.appName
  }));

  return (
    <Card
      title="Upload APK Files"
      style={{ maxWidth: 800, margin: "0 auto" }}
      extra={
        <Button onClick={() => navigate(PERMISSIONS.LIST_APP_INFO.route)}>
          Back to App Info
        </Button>
      }
    >
      <div style={{ marginBottom: 24 }}>
        <Alert
          message="APK Upload Guidelines"
          description={
            <div>
              <p>• Only APK files are allowed</p>
              <p>• Maximum file size: 100MB</p>
              <p>• Choose the correct app name</p>
              <p>• The uploaded APK will be available for download immediately</p>
            </div>
          }
          type="info"
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          fileType: appInfos.length > 0 ? appInfos[0].packageName : '',
          version: appInfos.length > 0 ? appInfos[0].version : '',
          versionCode: appInfos.length > 0 ? appInfos[0].versionCode : 0,
        }}
      >
        <Form.Item
          name="fileType"
          label="App Name"
          rules={[{ required: true, message: "Please select app name" }]}
        >
          <Select 
            placeholder="Select app name"
            loading={appInfoLoading}
            disabled={appInfoLoading || appInfos.length === 0}
            onChange={(selectedAppName) => {
              const selectedApp = appInfos.find(app => app.appName === selectedAppName);
              if (selectedApp) {
                form.setFieldsValue({
                  version: selectedApp.version,
                  versionCode: selectedApp.versionCode
                });
              }
            }}
          >
            {fileTypeOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="fileName"
          label="Custom File Name (Optional)"
          help="Leave empty to use original file name"
        >
          <input
            type="text"
            placeholder="Enter custom file name (without extension)"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </Form.Item>

        <Form.Item
          name="version"
          label="Version"
          rules={[{ required: true, message: "Please select version" }]}
        >
          <Input />
        </Form.Item>
        
        <Form.Item
          name="versionCode"
          label="Version Code"
          rules={[{ required: true, message: "Please select version code" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="APK File"
          required
          help="Select an APK file to upload"
        >
          <Dragger
            name="file"
            multiple={false}
            beforeUpload={beforeUpload}
            showUploadList={false}
            accept=".apk,application/vnd.android.package-archive"
            style={{
              border: selectedFile ? '2px solid #52c41a' : '1px dashed #d9d9d9',
              backgroundColor: selectedFile ? '#f6ffed' : '#fafafa',
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: selectedFile ? '#52c41a' : '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              {selectedFile ? 'File Selected' : 'Click or drag APK file to this area to upload'}
            </p>
            <p className="ant-upload-hint">
              {selectedFile 
                ? `${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`
                : 'Support for single APK file upload. Maximum size: 100MB'
              }
            </p>
          </Dragger>
        </Form.Item>

        {selectedFile && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Selected File: </Text>
            <Text>{selectedFile.name}</Text>
            <br />
            <Text type="secondary">
              Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </Text>
          </div>
        )}

        {loading && (
          <div style={{ marginBottom: 16 }}>
            <Text>Uploading... {uploadProgress}%</Text>
            <div
              style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                overflow: 'hidden',
                marginTop: '8px',
              }}
            >
              <div
                style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: '#1890ff',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!selectedFile}
              icon={<UploadOutlined />}
            >
              Upload APK
            </Button>
            <Button onClick={handleReset} disabled={loading}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UploadApkPage;
